import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useGlobalController, useRoute, useStateManager, useUIState } from "@bcm-dev/bcms-vite";
import update from "immutability-helper";
import Classes, { Account } from "model";
import React, { useCallback } from 'react';
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from 'react-dnd-html5-backend';

import { LeftColumnContainer, PageContainer, PageContentView, PageLayoutContainer, WidgetContainer, WidgetView } from 'components/shared';
import { InfoBanner } from 'components/shared/views/ConfirmBannerView';
import { PrimaryButton, SecondaryButton } from "components/ui";
import { SVGAsset } from "res";

import "./styles.css";
import "./mobile.css";



/**
 * @function EditAccountsViewPage
 * @return {React.Component}
 * @exported true
 * @description The page that shows the list of accounts
 */
export default function EditAccountsViewPage(props) {
    const globalController = useGlobalController();
    const navigate = useRoute();

    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";

    const setVisibility = evt => {
        return Account.initAll().reduce((manager, account) => {
            const { key } = getEditAccountItemConfig(account.id);
            return manager.update([ "ui", key, "isVisible" ], true);
        }, BCMSStateManagerCRUD.init());
    };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        setState,

        onIconToggle: key => value => {
          BCMSStateManagerCRUD.init(state)
            .update([ "ui", key, "isVisible" ], value)
            .submit(setState);
        },

        onReset: evt => {
            BCMSStateManagerCRUD.init(state)
              .combine(setVisibility())
              .update([ "data", "accounts" ], Account.initAll())
              .update([ "ui", "showResetBanner" ], true)
              .update([ "ui", "showVisibilityBanner" ], false)
              .submit(setState);
        },

        onShowAll: evt => {
            BCMSStateManagerCRUD.init(state)  
              .combine(setVisibility())
              .update([ "ui", "showVisibilityBanner" ], true)
              .update([ "ui", "showResetBanner" ], false)
              .submit(setState);
        },

        onBannerClose: property => evt => {
            BCMSStateManagerCRUD.init(state)
              .update([ "ui", property ], false)
              .submit(setState);
        },

        onCancel: evt => navigate("/accountSummary")
    });

    return (
        <PageContainer className={"edit-accounts-view-page"+className} state={ { data, ui } } controller={ controller }>
            <EditAccountsViewContent />
        </PageContainer>
    );
}

export const getDefaultPageData = () => ({
    accounts: Account.initAll()
});
export const getDefaultPageUI = () => ({
    showResetBanner: false,
    showVisibilityBanner: false
});

function EditAccountsViewContent(props) {
    const { data, ui, setState, onCancel, onReset, onShowAll, onBannerClose } = useStateManager();
    // React-dnd documentation: https://react-dnd.github.io/react-dnd/docs/overview
    // Immutability-helper documentation: https://www.npmjs.com/package/immutability-helper

    // Update function called when an item is being moved, updates the accounts
    // array to the updated list order
    const onMoveItem = useCallback((dragIndex, hoverIndex) => {
        BCMSStateManagerCRUD.init({ data, ui })
            .update([ "data", "accounts" ], update(
                data.accounts,
                {
                  $splice: [ // Updates the order of the array based on the dragIndex and hoverIndex
                    [ dragIndex, 1 ],
                    [ hoverIndex, 0, data.accounts[dragIndex] ]
                  ]
                }
            ))
            .submit(setState);
    }, []);
    const onRenderItem = useCallback((item, index) => {
      return <AccountTileView 
          key={ `Account_${ item.id }` } 
          account={ item }
          dataPoint={ "Account_Tile_View" }
          // Requuired for the drag and drop
          index={ index }
          id={ item.id }
          onMoveItem={ onMoveItem } />
    }, []);

    const accountItems = data.accounts.map((item, index) => onRenderItem(item, index), []);

    return (
        <PageContentView>
            <PageLayoutContainer>
                <LeftColumnContainer>
                    <WidgetContainer>
                        { ui.showResetBanner && <InfoBanner text="Preferences reset successfully." onClick={ onBannerClose("showResetBanner") } /> }
                        { ui.showVisibilityBanner && <InfoBanner text="All accounts set to visible state" onClick={ onBannerClose("showVisibilityBanner") } /> }

                        <WidgetView text="Edit Accounts View">
                            <p className="top-text">Please use the left icon <SVGAsset id="DragDropIcon" className="drag-drop-icon set-width inline-text-icon left-icon" /> to drag and drop accounts and change their order. Click the eye icon to hide/show accounts. You can come back and change this at any time. </p>
                            <div className="show-on-desktop">
                              <p className="top-text">To reorder items using your keyboard follow the instructions below:</p> 
                            
                              <ul>
                                  <li>Use tab keys to select an option</li>
                                  <li>Press space to enter into drag mode</li> 
                                  <li>Use up and down arrow keys to select a new position</li> 
                                  <li>Press Space to drop option in new position</li>
                              </ul> 
                            </div>
                            

                            {/* Account Rows */}
                            <div className="account-tile-container">
                                <DndProvider backend={ HTML5Backend }>
                                  { accountItems }
                                </DndProvider>
                            </div>

                            <div className="links-container flex-container flex-horizontal align-center">
                                <a className="reset-link set-width clickable" onClick={ onReset }>Reset</a>
                                <a className="show-all-link set-width clickable" onClick={ onShowAll }>Show All</a>
                            </div>

                            <div className="page-button-container flex-container flex-horizontal">
                                <SecondaryButton text="Cancel" dataPoint="Cancel" onClick={ onCancel } />

                                <DisabledPrompt>
                                    <PrimaryButton text="Save" dataPoint="Save" />
                                </DisabledPrompt>
                            </div>

                        </WidgetView>
                    </WidgetContainer>
                </LeftColumnContainer>
            </PageLayoutContainer>
        </PageContentView>
    );
}

/**
 * @function AccountTileView
 * @return {React.Component}
 * @exported false
 * @description The component that shows the account tile
 */

function AccountTileView(props){
    const account = props.account ?? Account.firstInstanceOf();
    const ref = React.useRef(null);

    // React-dnd documentation: https://react-dnd.github.io/react-dnd/docs/overview
    // Immutability-helper documentation: https://www.npmjs.com/package/immutability-helper
   
    // useDrop is the hook that sets a component as a droppable location
    const [ { handlerId }, drop ] = useDrop({
      accept: "card",
      collect: monitor => ({ handlerId: monitor.getHandlerId() }),
      hover: (item, monitor) => {
          if(!ref.current) { return; }

          const dragIndex = item.index;
          const hoverIndex = props.index;

          if(dragIndex === hoverIndex) { return; }

          const hoverBoundingReact = ref.current?.getBoundingClientRect();
          const hoverMiddleY = (hoverBoundingReact.bottom - hoverBoundingReact.top) / 4;

          const clientOffset = monitor.getClientOffset();
          const hoverClientY = clientOffset.y - hoverBoundingReact.top;

          if(dragIndex < hoverIndex && hoverClientY < hoverMiddleY) { return; }
          if(dragIndex > hoverIndex && hoverClientY > hoverMiddleY) { return; }

          props.onMoveItem(dragIndex, hoverIndex);
          item.index = hoverIndex;
      }
    });

    // useDrag hook sets the component as a draggable item
    const [ { isDragging }, drag, preview ] = useDrag({
      type: "card",
      item: () => ({ id: props.id, index: props.index }),
      collect: monitor => ({ isDragging: monitor.isDragging() })
    });
    const opacity = isDragging ? 0 : 1;

    preview(drop(ref));
  
    return(
      <div className="account-tile-view flex-container flex-horizontal align-center" ref={ ref } style={{ opacity }} data-handler-id={ handlerId }>
        <AccountTile account={ account } dataPoint={ props.dataPoint } drag={ drag } />
      </div>
    );
  }

function AccountTile(props) {
  const { onIconToggle } = useStateManager();
  const account = props.account ?? Account.firstInstanceOf();

  const { key, value } = getEditAccountItemConfig(account.id);
  const { config } = useUIState(key, value);

  const onIconClick = onIconToggle(key);
  const dragDropItemDataPoint = props.dataPoint ? { "data-point" : `${ props.dataPoint }_Drag_Drop_Icon_${ account.id }` } : {};
  const eyeIconDataPoint = props.dataPoint ? { "data-point" : `${ props.dataPoint }_Eye_Icon_${ account.id }` } : {};

  return (
    <>
      <span ref={ props.drag }>
        <SVGAsset id="DragDropIcon" className="drag-drop-icon set-width inline-text-icon left-icon clickable" { ...dragDropItemDataPoint } />
      </span>
      <p className="flex-width"><span className="account-nickname">{ account.nickname }</span> - <span>{ account.number }</span></p>
      
      { config.isVisible && <SVGAsset id="EyeIcon" className="set-width inline-text-icon right-icon eye-icon clickable" onClick={ evt => onIconClick(false) } { ...eyeIconDataPoint } /> }
      { !config.isVisible && <SVGAsset id="HideEyeIcon" className="set-width inline-text-icon right-icon eye-icon clickable" onClick={ evt => onIconClick(true) } { ...eyeIconDataPoint } /> }
    </>
  );
}

export const getEditAccountItemConfig = id => ({
    key: `EditAccount_${ id }`,
    value: {
      isVisible: true
    }
});
